import { FormEvent, ReactNode, useState } from "react";
import { SEO } from "@/components/SEO";
import { CheckCircle2, Loader2, Plus, Trash2 } from "lucide-react";

type TgForm = {
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  currentBiggestPain: string;
  servicesOffered: string;
  statesServed: string;
  monthlyTransactionVolume: string;
  notaryTeamSize: string;
  targetTimeline: string;
  preferredStartDate: string;
  existingDeveloperStatus: string;
  bookingTimesStatus: string;
  bookingIssues: string;
  paymentProcessor: string;
  currentMonthlyCosts: string;
  extraDocumentPaymentProcess: string;
  microsoftAdminOwner: string;
  azureStatus: string;
  teamsWorkflow: string;
  sumsubStatus: string;
  identityFailureProcess: string;
  setasignStatus: string;
  certificateOwner: string;
  documentStorageCurrent: string;
  customerPortalInterest: string;
  googleBusinessIssue: string;
  aiInterest: string;
  decisionMaker: string;
  proposalRecipient: string;
  supportExpectations: string;
  budgetRange: string;
  additionalNotes: string;
};

const initialForm: TgForm = {
  contactName: "Mirjana Pasic",
  contactEmail: "Mirjana@tgstrategypartners.com",
  contactPhone: "",
  currentBiggestPain: [
    "Current custom website/system works, but TG Notary Now needs faster technical support.",
    "David cannot easily book a customer while on the phone/mobile.",
    "Signing/notarization issues need quick live support when they happen during a call.",
    "Extra document payments are manual after the call today.",
  ].join("\n"),
  servicesOffered: [
    "Remote online notarization.",
    "Customers usually find TG Notary Now through Google search.",
    "Customer creates an account and books through the website/BookingTimes flow.",
    "Customer uploads documents before the appointment.",
    "Customer verifies identity, joins a Teams session, signs electronically, and receives completed documents by email.",
    "Notaries are partner notaries, not employees.",
  ].join("\n"),
  statesServed: "",
  monthlyTransactionVolume: "",
  notaryTeamSize: "",
  targetTimeline: [
    "No strict deadline.",
    "They want fixes as soon as practical while customer volume grows.",
    "First priority is takeover, stabilization, and support.",
  ].join("\n"),
  preferredStartDate: "",
  existingDeveloperStatus: "",
  bookingTimesStatus: "",
  bookingIssues: [
    "If a customer calls by phone, David has to be near a computer to enter/create the appointment.",
    "They need a faster mobile/admin booking workflow so he can book while talking to the customer.",
  ].join("\n"),
  paymentProcessor: "",
  currentMonthlyCosts: "",
  extraDocumentPaymentProcess: [
    "Customers choose/pay for the initial document count before the appointment.",
    "If they add another document during the live notarization call, there is no simple way to add that charge before completion.",
    "Today they invoice after the call or hold completed documents until payment is made.",
    "They want a cleaner in-session/add-on payment workflow.",
  ].join("\n"),
  microsoftAdminOwner: "",
  azureStatus: "",
  teamsWorkflow: "",
  sumsubStatus: "",
  identityFailureProcess: "",
  setasignStatus: "",
  certificateOwner: "",
  documentStorageCurrent: "",
  customerPortalInterest: [
    "Potential phase-two/three improvement.",
    "Recurring customers could benefit from account history/completed document access.",
    "Legal/compliance needs to confirm if customer portal storage is allowed and for how long.",
  ].join("\n"),
  googleBusinessIssue: [
    "Google Business Profile has been created but keeps getting suspended or not verified.",
    "David has submitted verification videos multiple times.",
    "They want help resolving this.",
  ].join("\n"),
  aiInterest: [
    "Interested in AI chat and possibly AI phone/intake.",
    "Likely phase two.",
    "First priority is optimizing/stabilizing the website/system.",
  ].join("\n"),
  decisionMaker: "David is the owner/decision maker. Mirjana is the middle person and can collect answers from David.",
  proposalRecipient: "",
  supportExpectations: "",
  budgetRange: "",
  additionalNotes: "Prefilled by Better Systems AI from the June 29, 2026 discovery call. Please review, correct anything that is wrong, and complete every highlighted field before submitting. No passwords, API keys, private credentials, or sensitive customer documents should be entered here.",
};

const requiredFields: Array<keyof TgForm> = [
  "contactName",
  "contactEmail",
  "currentBiggestPain",
  "statesServed",
  "monthlyTransactionVolume",
  "notaryTeamSize",
  "preferredStartDate",
  "existingDeveloperStatus",
  "bookingTimesStatus",
  "paymentProcessor",
  "currentMonthlyCosts",
  "microsoftAdminOwner",
  "azureStatus",
  "teamsWorkflow",
  "sumsubStatus",
  "identityFailureProcess",
  "setasignStatus",
  "certificateOwner",
  "documentStorageCurrent",
  "decisionMaker",
  "proposalRecipient",
  "supportExpectations",
];

const requiredFieldLabels: Partial<Record<keyof TgForm, string>> = {
  contactName: "Contact name",
  contactEmail: "Contact email",
  currentBiggestPain: "Biggest current pain",
  statesServed: "States served",
  monthlyTransactionVolume: "Monthly transaction volume",
  notaryTeamSize: "Notary/team size",
  preferredStartDate: "Preferred start date",
  existingDeveloperStatus: "Developer/access handoff",
  bookingTimesStatus: "BookingTimes status",
  paymentProcessor: "Payment processor",
  currentMonthlyCosts: "Current software/vendor costs",
  microsoftAdminOwner: "Microsoft admin owner",
  azureStatus: "Azure status",
  teamsWorkflow: "Teams workflow",
  sumsubStatus: "Sumsub status",
  identityFailureProcess: "Identity failure process",
  setasignStatus: "Setasign/eSeal status",
  certificateOwner: "Certificate owner/storage",
  documentStorageCurrent: "Document storage/current records",
  decisionMaker: "Decision maker/signer",
  proposalRecipient: "Proposal recipient",
  supportExpectations: "Support expectations",
};

export default function TgNotaryQuestionnairePage() {
  const [form, setForm] = useState<TgForm>(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submissionId, setSubmissionId] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof TgForm, string>>>({});

  const update = (field: keyof TgForm, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    setError("");
    if (fieldErrors[field]) {
      setFieldErrors((current) => {
        const next = { ...current };
        delete next[field];
        return next;
      });
    }
  };

  const validate = () => {
    const errors: Partial<Record<keyof TgForm, string>> = {};

    requiredFields.forEach((field) => {
      if (!form[field].trim()) {
        errors[field] = `${requiredFieldLabels[field] || "This field"} is required.`;
      }
    });

    if (form.contactEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactEmail.trim())) {
      errors.contactEmail = "Enter a valid contact email.";
    }

    return errors;
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setFieldErrors(validationErrors);
      setError("Please complete the highlighted required fields before submitting.");
      return;
    }

    setSubmitting(true);
    setError("");
    setFieldErrors({});
    try {
      const response = await fetch("/api/forms/tg-notary-questionnaire", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          ...(new URLSearchParams(window.location.search).get("test") === "1"
            ? { testMode: true, skipEmails: true }
            : {}),
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.success || !data.submissionId) {
        throw new Error(data.message || "Submission failed.");
      }
      setSubmissionId(data.submissionId);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submissionId) {
    return (
      <div className="min-h-screen bg-[#F7F4EE] px-4 py-20 text-[#102033]">
        <SEO title="TG Notary Questionnaire Received" robots="noindex, nofollow" />
        <main className="mx-auto flex max-w-2xl flex-col items-center rounded-lg border border-[#102033]/12 bg-white p-8 text-center shadow-[0_18px_50px_rgba(16,32,51,0.08)]">
          <CheckCircle2 className="mb-4 h-14 w-14 text-[#176946]" />
          <h1 className="mb-3 text-3xl font-black">Questionnaire received</h1>
          <p className="text-lg leading-8 text-[#536276]">
            Thank you. Better Systems AI received the TG Notary Now technical questionnaire.
          </p>
          <p className="mt-5 rounded-md bg-[#F7F4EE] px-4 py-2 text-sm font-bold">BSA submission ID: {submissionId}</p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F4EE] text-[#102033]">
      <SEO
        title="TG Notary Now Technical Questionnaire"
        description="Technical questionnaire for TG Notary Now system takeover and stabilization."
        robots="noindex, nofollow"
      />
      <main className="mx-auto max-w-5xl px-4 py-12">
        <header className="mb-8">
          <div className="mb-3 inline-flex rounded-full border border-[#102033]/15 bg-white px-3 py-2 text-xs font-extrabold uppercase text-[#102033]/70">
            Better Systems AI
          </div>
          <h1 className="mb-4 text-4xl font-black leading-tight md:text-5xl">TG Notary Now technical questionnaire</h1>
          <p className="max-w-3xl text-lg leading-8 text-[#536276]">
            We filled in what we understood from the call. The highlighted fields are the information we still need from David/Mirjana before we can quote the takeover and support path.
          </p>
        </header>

        <form onSubmit={submit} className="space-y-6 rounded-lg border border-[#102033]/12 bg-white p-5 shadow-[0_18px_50px_rgba(16,32,51,0.08)] md:p-8">
          <div className="rounded-lg border-2 border-[#F26B1F] bg-[#FFF7ED] p-4">
            <p className="text-sm font-black uppercase tracking-wide text-[#9A3412]">What we need from you</p>
            <p className="mt-2 text-sm leading-6 text-[#7C2D12]">
              Please review the prefilled call notes, correct anything that is wrong, and complete every highlighted required field. If you do not know an answer yet, write who owns it or what needs to be checked. Do not enter passwords, API keys, or private credentials.
            </p>
          </div>

          <Section title="Contact">
            <div className="grid gap-4 md:grid-cols-3">
              <Input label="Name" value={form.contactName} onChange={(value) => update("contactName", value)} error={fieldErrors.contactName} required />
              <Input label="Email" type="email" value={form.contactEmail} onChange={(value) => update("contactEmail", value)} error={fieldErrors.contactEmail} helper="Required so we can send the confirmation and quote follow-up." priority required />
              <Input label="Phone" value={form.contactPhone} onChange={(value) => update("contactPhone", value)} helper="Optional, but useful for support coordination." />
            </div>
          </Section>

          <Section title="Business and Current Pain">
            <LineItems label="Pain points" value={form.currentBiggestPain} onChange={(value) => update("currentBiggestPain", value)} error={fieldErrors.currentBiggestPain} helper="Prefilled from the call. Add, remove, or correct each pain point." required />
            <div className="grid gap-4 md:grid-cols-2">
              <LineItems label="Services / workflow steps" value={form.servicesOffered} onChange={(value) => update("servicesOffered", value)} helper="Prefilled from the call. Add states served, service limits, or anything we missed." />
              <LineItems label="Timeline / priorities" value={form.targetTimeline} onChange={(value) => update("targetTimeline", value)} helper="Prefilled from the call. Add urgency, deadline, or launch target if one exists." />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <Textarea label="States served" value={form.statesServed} onChange={(value) => update("statesServed", value)} placeholder="Needed: states/jurisdictions served today and any states planned next." helper="Required so the quote respects RON rules, notary coverage, and compliance scope." error={fieldErrors.statesServed} priority required />
              <Textarea label="Monthly transaction volume" value={form.monthlyTransactionVolume} onChange={(value) => update("monthlyTransactionVolume", value)} placeholder="Needed: average monthly bookings, notarizations, document uploads, failed sessions, and expected growth." helper="Required for support load, hosting, admin dashboard, and maintenance pricing." error={fieldErrors.monthlyTransactionVolume} priority required />
              <Textarea label="Notary/team size" value={form.notaryTeamSize} onChange={(value) => update("notaryTeamSize", value)} placeholder="Needed: number of notaries today, expected notaries in 90 days, admin users, and whether partner notaries need separate accounts." helper="Required for Microsoft, certificate, access, and support planning." error={fieldErrors.notaryTeamSize} priority required />
              <Textarea label="Preferred start date" value={form.preferredStartDate} onChange={(value) => update("preferredStartDate", value)} placeholder="Needed: ideal start date, any urgent issue, and whether there is a hard customer/business deadline." helper="Required so we can price a normal start vs. rush takeover." error={fieldErrors.preferredStartDate} priority required />
            </div>
            <Textarea
              label="Developer / access handoff status"
              value={form.existingDeveloperStatus}
              onChange={(value) => update("existingDeveloperStatus", value)}
              placeholder="Needed: who owns the source code, repo, hosting, database, domain/DNS, vendor accounts, and what the current developer has already handed over."
              helper="This is required for takeover pricing. Do not paste passwords or API keys."
              error={fieldErrors.existingDeveloperStatus}
              priority
              required
            />
          </Section>

          <Section title="Booking and Payments">
            <Textarea
              label="BookingTimes status"
              value={form.bookingTimesStatus}
              onChange={(value) => update("bookingTimesStatus", value)}
              placeholder="Needed: active plan, account owner, API/webhook access, service/pricing setup, availability rules, and whether BookingTimes should stay."
              helper="This tells us whether we stabilize BookingTimes or replace parts of the booking workflow."
              error={fieldErrors.bookingTimesStatus}
              priority
              required
            />
            <LineItems label="Booking issues" value={form.bookingIssues} onChange={(value) => update("bookingIssues", value)} helper="Prefilled from the call. Add exact failure examples, screenshots, or steps if available." />
            <div className="grid gap-4 md:grid-cols-2">
              <Textarea
                label="Payment processor"
                value={form.paymentProcessor}
                onChange={(value) => update("paymentProcessor", value)}
                placeholder="Needed: Stripe, Square, BookingTimes payments, or other processor; account owner; refund process; where payment status is visible today."
                helper="Required so we can quote the add-on document payment fix correctly."
                error={fieldErrors.paymentProcessor}
                priority
                required
              />
              <Textarea
                label="Current software/vendor costs"
                value={form.currentMonthlyCosts}
                onChange={(value) => update("currentMonthlyCosts", value)}
                placeholder="Needed: monthly/annual costs for BookingTimes, Microsoft 365, Azure, Sumsub, Setasign, hosting, domain, support, and any current developer retainer."
                helper="Required so the proposal separates BSA labor from pass-through vendor costs."
                error={fieldErrors.currentMonthlyCosts}
                priority
                required
              />
              <LineItems label="Extra document payment process" value={form.extraDocumentPaymentProcess} onChange={(value) => update("extraDocumentPaymentProcess", value)} helper="Prefilled from the call. Add how often this happens and the ideal outcome." />
            </div>
          </Section>

          <Section title="Microsoft, Identity, Signing, Records">
            <div className="grid gap-4 md:grid-cols-2">
              <Textarea label="Microsoft admin owner" value={form.microsoftAdminOwner} onChange={(value) => update("microsoftAdminOwner", value)} placeholder="Needed: Microsoft tenant name, global admin owner, and whether each notary has a Microsoft 365 account." helper="Required for Teams/Graph/Azure access planning." error={fieldErrors.microsoftAdminOwner} priority required />
              <Textarea label="Azure status" value={form.azureStatus} onChange={(value) => update("azureStatus", value)} placeholder="Needed: Azure subscription owner, Blob Storage, Key Vault, app registrations, Graph permissions, and current environment access." helper="Required if Azure is part of the current backend." error={fieldErrors.azureStatus} priority required />
              <Textarea label="Teams workflow" value={form.teamsWorkflow} onChange={(value) => update("teamsWorkflow", value)} placeholder="Needed: how Teams meetings are created, organizer account, recording rules, where recordings go, and what happens when a session fails." helper="Required for remote notarization session support." error={fieldErrors.teamsWorkflow} priority required />
              <Textarea label="Sumsub identity verification" value={form.sumsubStatus} onChange={(value) => update("sumsubStatus", value)} placeholder="Needed: active account, plan, checks enabled, KBA/manual review, webhooks/API, and where verification proof/results are stored." helper="Required for ID verification/compliance workflow." error={fieldErrors.sumsubStatus} priority required />
              <Textarea label="Identity failure process" value={form.identityFailureProcess} onChange={(value) => update("identityFailureProcess", value)} placeholder="Needed: what happens when ID/liveness/KBA fails, who can override, and what customer/notary sees." helper="Required for support and compliance handling." error={fieldErrors.identityFailureProcess} priority required />
              <Textarea label="Setasign/eSeal status" value={form.setasignStatus} onChange={(value) => update("setasignStatus", value)} placeholder="Needed: active Setasign account, API access, signing workflow, final PDF generation, and whether PAdES-LTV is required." helper="Required for signing/eSeal scope." error={fieldErrors.setasignStatus} priority required />
              <Textarea label="Certificate owner/storage" value={form.certificateOwner} onChange={(value) => update("certificateOwner", value)} placeholder="Needed: who owns each notary digital certificate/eSeal, where keys/certificates live, and who can manage them." helper="Do not paste certificate files or secrets here." error={fieldErrors.certificateOwner} priority required />
              <Textarea label="Current document storage" value={form.documentStorageCurrent} onChange={(value) => update("documentStorageCurrent", value)} placeholder="Needed: where uploaded documents, final PDFs, recordings, identity results, journal/audit log, and backups are stored today." helper="Required for retention and customer portal planning." error={fieldErrors.documentStorageCurrent} priority required />
            </div>
            <LineItems label="Customer portal interest / legal concerns" value={form.customerPortalInterest} onChange={(value) => update("customerPortalInterest", value)} helper="Prefilled from the call. Please add legal/compliance constraints or attorney guidance if known." />
          </Section>

          <Section title="Growth and Decision">
            <div className="grid gap-4 md:grid-cols-2">
              <LineItems label="Google Business Profile issue" value={form.googleBusinessIssue} onChange={(value) => update("googleBusinessIssue", value)} helper="Prefilled from the call. Add account owner, verification history, or support case details if available." />
              <LineItems label="AI chat / AI phone interest" value={form.aiInterest} onChange={(value) => update("aiInterest", value)} helper="Prefilled as phase two. Add any must-have AI intake use cases." />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <Input label="Decision maker / signer" value={form.decisionMaker} onChange={(value) => update("decisionMaker", value)} helper="Confirm who can approve the proposal and sign the agreement." error={fieldErrors.decisionMaker} priority required />
              <Input label="Proposal recipient" value={form.proposalRecipient} onChange={(value) => update("proposalRecipient", value)} helper="Who should receive the quote/proposal and billing agreement?" error={fieldErrors.proposalRecipient} priority required />
              <Textarea label="Support expectations" value={form.supportExpectations} onChange={(value) => update("supportExpectations", value)} placeholder="Needed: expected response time, live-session emergency support needs, normal support hours, and preferred channel." helper="Required for monthly maintenance pricing." error={fieldErrors.supportExpectations} priority required />
              <Textarea label="Budget comfort / approval range" value={form.budgetRange} onChange={(value) => update("budgetRange", value)} placeholder="Optional but helpful: known budget, approval range, or what number would need extra approval." helper="Optional. Helps us avoid proposing a phase one that is too small or too large." />
            </div>
            <Textarea label="Additional notes" value={form.additionalNotes} onChange={(value) => update("additionalNotes", value)} helper="Anything else we should know before quoting." />
          </Section>

          {error && <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">{error}</div>}

          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center justify-center rounded-md bg-[#102033] px-6 py-3 text-sm font-extrabold uppercase text-white disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting</> : "Submit questionnaire"}
          </button>
        </form>
      </main>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="border-b border-[#102033]/10 pb-6 last:border-b-0">
      <h2 className="mb-4 text-xl font-black">{title}</h2>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Input({
  label,
  value,
  onChange,
  required,
  type = "text",
  helper,
  error,
  priority,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  type?: string;
  helper?: string;
  error?: string;
  priority?: boolean;
}) {
  return (
    <label className="block">
      <span className="mb-2 flex items-center gap-2 text-sm font-extrabold text-[#102033]">
        {label}{required ? " *" : ""}
        {priority && <span className="rounded-sm bg-[#F26B1F] px-2 py-0.5 text-[10px] uppercase tracking-wide text-white">Needed</span>}
      </span>
      {helper && <span className="mb-2 block text-xs font-semibold leading-5 text-[#536276]">{helper}</span>}
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={Boolean(error)}
        className={`w-full rounded-md border px-3 py-3 text-[#102033] outline-none focus:border-[#102033] ${
          error
            ? "border-red-500 bg-red-50"
            : priority && !value.trim()
              ? "border-[#F26B1F] bg-[#FFF7ED]"
              : "border-[#102033]/15 bg-[#FFFDF8]"
        }`}
        required={required}
      />
      {error && <span className="mt-2 block text-xs font-bold text-red-700">{error}</span>}
    </label>
  );
}

function Textarea({
  label,
  value,
  onChange,
  required,
  placeholder,
  helper,
  error,
  priority,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  placeholder?: string;
  helper?: string;
  error?: string;
  priority?: boolean;
}) {
  return (
    <label className="block">
      <span className="mb-2 flex items-center gap-2 text-sm font-extrabold text-[#102033]">
        {label}{required ? " *" : ""}
        {priority && <span className="rounded-sm bg-[#F26B1F] px-2 py-0.5 text-[10px] uppercase tracking-wide text-white">Needed</span>}
      </span>
      {helper && <span className="mb-2 block text-xs font-semibold leading-5 text-[#536276]">{helper}</span>}
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        rows={4}
        aria-invalid={Boolean(error)}
        className={`w-full resize-y rounded-md border px-3 py-3 text-[#102033] outline-none focus:border-[#102033] ${
          error
            ? "border-red-500 bg-red-50"
            : priority && !value.trim()
              ? "border-[#F26B1F] bg-[#FFF7ED]"
              : "border-[#102033]/15 bg-[#FFFDF8]"
        }`}
        required={required}
      />
      {error && <span className="mt-2 block text-xs font-bold text-red-700">{error}</span>}
    </label>
  );
}

function LineItems({
  label,
  value,
  onChange,
  required,
  helper,
  error,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  helper?: string;
  error?: string;
}) {
  const items = value.split("\n");

  const updateItem = (index: number, nextValue: string) => {
    const next = [...items];
    next[index] = nextValue;
    onChange(next.join("\n"));
  };

  const addItem = () => {
    onChange([...items, ""].join("\n"));
  };

  const removeItem = (index: number) => {
    const next = items.filter((_, itemIndex) => itemIndex !== index);
    onChange((next.length ? next : [""]).join("\n"));
  };

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-sm font-extrabold text-[#102033]">{label}{required ? " *" : ""}</div>
          {helper && <div className="mt-1 text-xs font-semibold leading-5 text-[#536276]">{helper}</div>}
        </div>
        <button
          type="button"
          onClick={addItem}
          className="inline-flex items-center gap-2 rounded-md border border-[#102033]/15 bg-white px-3 py-2 text-xs font-extrabold uppercase text-[#102033] hover:bg-[#F7F4EE]"
        >
          <Plus className="h-4 w-4" />
          Add line
        </button>
      </div>
      <div className={`space-y-2 rounded-md border p-3 ${
        error ? "border-red-500 bg-red-50" : "border-[#102033]/12 bg-[#FFFDF8]"
      }`}>
        {items.map((item, index) => (
          <div key={index} className="flex items-start gap-2">
            <div className="mt-3 h-2 w-2 shrink-0 rounded-full bg-[#102033]/45" />
            <input
              type="text"
              value={item}
              onChange={(event) => updateItem(index, event.target.value)}
              className="min-h-11 flex-1 rounded-md border border-[#102033]/12 bg-white px-3 py-2 text-sm text-[#102033] outline-none focus:border-[#102033]"
            />
            <button
              type="button"
              onClick={() => removeItem(index)}
              aria-label={`Remove ${label} line ${index + 1}`}
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-[#102033]/12 bg-white text-[#536276] hover:bg-red-50 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
      {error && <span className="mt-2 block text-xs font-bold text-red-700">{error}</span>}
    </div>
  );
}
